/** An app specific context */

import type { RootState, TunaThunkDispatch, UserConfigService } from '@tunasong/redux'
import type { Unsubscribe } from 'redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { createContext, useContext } from 'react'
import { type $ZodType } from 'zod/v4/core'
import type { Graph } from '@tunasong/graph-lib'
import type { ExecuteAIPrompt } from './ai-prompt-types.js'

export interface DefaultGlobalContext {
  [key: string]: unknown

  /** Config Store */
  configService: UserConfigService

  /** Redux store */
  getState: () => RootState
  /** Redux dispatch */
  dispatch: TunaThunkDispatch
  /** Redux subscribe to changes */
  subscribe: (cb: () => void) => Unsubscribe

  /**
   * Navigate to an entity. `options.layout` selects a plugin layout by name
   * (e.g. 'Performance') — matches ui-lib's NavFn, which is what the app
   * injects here. Structural type (not imported) because plugin-lib cannot
   * depend on ui-lib.
   */
  navigateToEntity: (entity: Persisted<Entity>, options?: { layout?: string }) => void

  graph: Graph

  /** Execute an AI prompt. Provided by the app layer to integrate with the AI backend. */
  executeAIPrompt?: ExecuteAIPrompt

  prompt: <T extends Record<string, unknown>>({
    schema,
    title,
    defaultValues,
  }: {
    schema: $ZodType<T>
    title: string
    defaultValues?: Record<string, unknown>
  }) => Promise<T | null>
}

export const GlobalAppContext = createContext<DefaultGlobalContext | null>(null)

export function useGlobalAppContext<TGlobalAppContext extends DefaultGlobalContext>() {
  const context = useContext(GlobalAppContext)
  if (!context) {
    throw new Error('GlobalAppContext is not set for this application')
  }

  return context as TGlobalAppContext
}
